import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { showError } from '../utils/toast';
import { hasReadPermission } from '../utils/modulePermissions';

/**
 * Module to route mapping
 * Maps module permissions to their corresponding routes
 */
const MODULE_ROUTES = {
  contacts: ['/contacts'],
  donation_pledge: ['/donations'],
  donation_reminder: ['/reminders'],
  donation_transaction: ['/transactions'],
  email_marketing: ['/campaigns'],
  whatsapp_marketing: ['/campaigns'],
  email_template: ['/email-templates'],
  whatsapp_template: ['/whatsapp-templates'],
  tags: ['/tags'],
  donation_scheme: ['/donation-schemas'],
  trust_master: ['/trust-masters'],
};

/**
 * Get all matching permissions for a route
 * A route like /campaigns can be accessed by multiple permissions (email_marketing OR whatsapp_marketing)
 * @param {string} path - Route path
 * @returns {string[]} - Array of matching module permissions (empty if no permission needed)
 */
export const getRoutePermissions = (path) => {
  // Remove leading slash and get base path
  const basePath = '/' + path.replace(/^\//, '').split('/')[0];

  // Find ALL matching modules (not just the first one)
  const matchingModules = [];
  for (const [module, routes] of Object.entries(MODULE_ROUTES)) {
    if (routes.some((route) => basePath.startsWith(route))) {
      matchingModules.push(module);
    }
  }

  return matchingModules;
};

/**
 * Check if user can access a route
 * @param {Object} user - User object
 * @param {string} path - Route path
 * @returns {boolean}
 */
export const canAccessRoute = (user, path) => {
  if (!user) return false;
  if (user.role === 'ADMIN') return true;

  const requiredPermissions = getRoutePermissions(path);
  if (requiredPermissions.length === 0) return true; // No permission required

  // User needs read permission for ANY of the matching modules
  return requiredPermissions.some((permission) => hasReadPermission(user, permission));
};

/**
 * Hook for permission-aware navigation
 * Blocks navigation if user doesn't have permission
 */
export const usePermissionNavigation = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const navigateWithPermission = (path, options = {}) => {
    if (!canAccessRoute(user, path)) {
      showError('You do not have permission to access this page');
      return false;
    }

    navigate(path, options);
    return true;
  };

  return navigateWithPermission;
};

export default usePermissionNavigation;
